import React, { useCallback, useState } from "react";
import styled, { CSSObject } from "styled-components";
import { useDispatch } from "react-redux";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { AlertNotification, Button, Popup } from "@commonsku/styles";

import { BASE_ZINDEX } from "../popup-factory";

import { PopupHeaderWithCloseIcon } from "./FormPopup";

import { CreditMemo } from "../types/credit_memo";
import { retrieveCreditMemo } from "../redux/credit_memos";
import CreditMemoPopup from "./credit-memo/CreditMemoPopup";

import { VendorCredit } from "../types/vendor_credit";
import { retrieveVendorCredit } from "../redux/vendor_credits";
import VendorCreditPopup from "./vendor-credit/VendorCreditPopup";

import { Bill } from "../types/bills";
import { retrieveBill } from "../redux/bills";
import BillPopup from "./bills/BillPopup";

import { createEnumFactoryWithSchema } from "../types/utils";
import { ENTITY_TYPES } from "../types/entities";

type CauseSchema = {
  label: string;
}

const createCauseEnum = createEnumFactoryWithSchema<CauseSchema>();
const CAUSE_TYPES = createCauseEnum({
  "CREDIT-MEMO": { label: "Credit Memo" },
  "VENDOR-CREDIT": { label: "Vendor Credit" },
  "BILL": { label: "Bill" },
  "PURCHASE-ORDER": { label: "Purchase Order" },
});

interface Cause {
  type: keyof typeof CAUSE_TYPES.types;
  ids: string[];
}

const ActionContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 0.5rem;
`;

export const NotificationContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1rem;
  overflow-y: auto;
`;

const POPUP_WIDTH = '36rem';
const POPUP_HEIGHT = '17rem';
const POPUP_STYLE = {
  display: "flex",
  flexFlow: "column nowrap",
  justifyContent: "flex-start",
  gap: "1rem",
} as CSSObject;
const POPUP_CONTENT_STYLE = {
  display: "flex",
  flexFlow: "column nowrap",
  justifyContent: "space-between",
  flexGrow: 1,
  gap: "1rem",
  overflowY: "hidden",
} as React.CSSProperties;


export default function NotifyFailurePopup(
  {
    entityType,
    title,
    message,
    onClose,
    cause,
  }:
  {
    entityType: keyof typeof ENTITY_TYPES.types,
    title: React.ReactNode,
    message: React.ReactNode,
    onClose: () => void,
    cause?: Cause
  }
) {
  const dispatch = useDispatch();
  const [causeView, setCauseView] = useState<React.ReactNode | undefined>();
  const handleClickView = useCallback(async () => {
    const id = cause?.ids?.[0] ?? null;
    if (!id) {
      return;
    }
    switch (cause.type) {
      case "CREDIT-MEMO":
        const creditMemo = await retrieveCreditMemo(id)(dispatch);
        setCauseView(<CreditMemoPopup creditMemo={creditMemo} onClose={onClose} isDisabled />);
        return;
      case "VENDOR-CREDIT":
        const vendorCredit = await retrieveVendorCredit(id)(dispatch);
        setCauseView(<VendorCreditPopup vendorCredit={vendorCredit} onClose={onClose} isDisabled />);
        return;
      case "BILL":
        const bill = await retrieveBill(id)(dispatch);
        setCauseView(<BillPopup bill={bill} onClose={onClose} isDisabled />);
        return;
      case "PURCHASE-ORDER":
        window.open(`/public_purchase_order.php?id=${id}`, "_blank");
        return;
      default:
        return;
    }
  }, [cause]);

  return (
    <Popup
      closeOnEsc
      closeOnClickOutside={false}
      onClose={onClose}
      header={<PopupHeaderWithCloseIcon title={title} onPopupClose={onClose} />}
      width={POPUP_WIDTH}
      height={POPUP_HEIGHT}
      style={POPUP_STYLE}
      popupContentStyle={POPUP_CONTENT_STYLE}
    >
      {causeView}
      <NotificationContainer>
        <AlertNotification alertType="error">{message}</AlertNotification>
      </NotificationContainer>
      <ActionContainer>
        <Button variant={!!cause ? "secondary" : "primary"} onClick={onClose}>
          Cancel
        </Button>
        {!!cause && (
          <Button onClick={handleClickView}>
            View {CAUSE_TYPES.types[cause.type].label}
          </Button>
        )}
      </ActionContainer>
    </Popup>
  );
}
